import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Container, Row } from "react-bootstrap";
import { DatePick, TextInputForm } from "../components/Forms";
import PageTitle from "../components/PageTitle";
import { Buttons } from "../components/Buttons";
import TableUI from "../components/TableUI";
import { useNavigate } from "react-router-dom";
import { fetchInvoice } from "../slices/invoiceSlice";
import { jsPDF } from "jspdf";

const OverallReports = () => {
  const dispatch = useDispatch();
  const invoice = useSelector((state) => state.invoice.invoices); // Fixed: Correct selector for invoices array
  const navigate = useNavigate();

  const [filteredData, setFilteredData] = useState(invoice);
  const [filters, setFilters] = useState({
    agent_name: "",
    from_date: "",
    to_date: "",
    customer_name: "", // Example: Adding another dynamic filter
  });

  // Fetching invoice data on component mount
  useEffect(() => {
    dispatch(fetchInvoice());
  }, [dispatch]);

  // Sync filteredData with the latest invoice data from Redux
  useEffect(() => {
    setFilteredData(invoice);
  }, [invoice]);

  // Extract unique agent names from invoice data
  const agents = [...new Set(invoice.map((item) => item.agent_name))];

  const handleFilterChange = () => {
    let filtered = invoice; // Fixed: Use 'invoice' (full list) instead of undefined

    // Iterate over filters dynamically and apply them
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        if (key === 'from_date' || key === 'to_date') {
          filtered = filtered.filter(item => {
            const invoiceDate = new Date(item.invoice_date || item.create_at);
            if (key === 'from_date' && filters[key]) {
              return invoiceDate >= new Date(filters[key]);
            }
            if (key === "to_date" && filters[key]) {
              return invoiceDate <= new Date(filters[key]);
            }
            return true;
          });
        } else {
          filtered = filtered.filter((item) =>
            item[key].toLowerCase().includes(filters[key].toLowerCase())
          );
        }
      }
    });

    setFilteredData(filtered);
  };

  const handleRowClick = (row) => {
    // Navigate to detailed report with selected agent_name and customer_name
    navigate("/reports/detailed-report", {
      state: {
        agent_name: row.agent_name,
        customer_name: row.customer_name,
        data: row,
      },
    });
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.text("Invoice Report", 20, 10);

    let yPosition = 20;
    filteredData.forEach((item) => {
      yPosition += 10;
      doc.text(
        `Agent: ${item.agent_name}, Party: ${item.customer_name}, Total: ₹${item.grand_total}`,
        20,
        yPosition
      );
    });

    doc.save("invoice_report.pdf");
  };

  // Group the invoice data by agent_name and customer_name
  const groupData = () => {
    return filteredData.reduce((acc, item) => {
      const key = `${item.agent_name}-${item.customer_name}`;
      if (!acc[key]) {
        acc[key] = {
          agent_name: item.agent_name,
          customer_name: item.customer_name,
          total: 0,
          invoices: [],
        };
      }
      acc[key].total += item.grand_total;
      acc[key].invoices.push(item);
      return acc;
    }, {});
  };

  const groupedData = groupData();

  const reportHead = ["Agent Name", "Party Name", "Total"];
  const reportData = Object.values(groupedData).map((item) => ({
    values: [item.agent_name, item.customer_name, item.total],
    agent_name: item.agent_name,
    customer_name: item.customer_name,
  }));

  return (
    <div id="main">
      <Container>
        <Row>
          <Col lg="3" className="py-3">
            <PageTitle PageTitle="Report" showButton={false} />
          </Col>
          <Col lg="9" className="py-3 text-end">
            <Buttons
              label="Download PDF"
              classname="crud-btn"
              onClick={generatePDF}
            />
          </Col>
          <Col lg="3" className="py-3">
            <TextInputForm
              PlaceHolder="Search Agents"
              textlabel={<>Agent Name</>}
              value={filters.agent_name}
              onChange={(e) =>
                setFilters({ ...filters, agent_name: e.target.value })
              }
            />
          </Col>
          <Col lg="3" className="py-3">
            <TextInputForm
              PlaceHolder="Search Customer"
              textlabel={<>Customer Name</>}
              value={filters.customer_name}
              onChange={(e) =>
                setFilters({ ...filters, customer_name: e.target.value })
              }
            />
          </Col>
          <Col lg="3" className="py-3">
            <DatePick
              textlabel={<>From</>}
              value={filters.from_date}
              onChange={(e) =>
                setFilters({ ...filters, from_date: e.target.value })
              }
            />
          </Col>
          <Col lg="3" className="py-3">
            <DatePick
              textlabel={<>To</>}
              value={filters.to_date}
              onChange={(e) =>
                setFilters({ ...filters, to_date: e.target.value })
              }
            />
          </Col>
          <Col lg="3" className="py-3 text-end align-self-end">
            <Buttons
              label="Submit"
              classname="crud-btn"
              onClick={handleFilterChange}
            />
          </Col>
          <Col lg="12" className="py-3">
            <TableUI
              headers={reportHead}
              body={reportData}
              showActionColumn={false}
              onClick={handleRowClick}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default OverallReports;
